import type { LoaderFunctionArgs } from '~/types/route-args';
import type {
    GetProductGalleryQuery,
    GetProductGalleryQueryVariables,
} from 'generated/graphql';
import { GetProductGalleryDocument } from 'generated/documents/GetProductGalleryDocument';
import getGallerySlides from '@modules/product/util/get-gallery-slides';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import { getSlideRatio } from '~/ui-rocco/pages/details-page/revamp/sections/gallery-geometry';

export const NEXT_PRODUCT_GALLERY_API_ROUTE = '/remix-api/product/next-gallery';

/** One slide of a product's gallery, as the gallery lays it out. */
export interface NextGallerySlide {
    id: number;
    /** The rendition the gallery paints, so the browser has it cached once the page turns. */
    src: string;
    /** The canonical width ÷ height the slide's card snaps to. */
    ratio: number;
    /** A clear-cut render: contained on the neutral fill and multiplied into it. */
    multiplied: boolean;
}

export interface NextGallery {
    /** The product the row belongs to, so a stale answer is never laid out under another product's name. */
    link: string;
    title: string;
    slides: NextGallerySlide[];
}

/**
 * The gallery row a product page opens on, for the foot of the page before it.
 *
 * The foot grows the next product's gallery into place and then turns the
 * page, and the turn is only invisible while the row it grew is the row the
 * next page paints: same renditions, same shapes, same order. Those come from
 * the product itself rather than from its listing tile, which carries a
 * different crop of a different size.
 */
export const loader = async ({
    request,
    context,
}: LoaderFunctionArgs): Promise<NextGallery | null> => {
    const link = new URL(request.url).searchParams.get('link');
    if (!link) throw new Response('Missing link', { status: 400 });

    const { data } = await context.client.query<
        GetProductGalleryQuery,
        GetProductGalleryQueryVariables
    >({
        query: GetProductGalleryDocument,
        variables: { link },
    });

    const node = data?.Directory?.Product;
    if (node?.__typename !== 'Product') return null;

    const slides = getGallerySlides({
        LibraryImages: node.LibraryImages ?? [],
        LibraryVideos: node.LibraryVideos ?? [],
        DiagramImages: [],
    })
        .map((slide) => ({
            id: slide.ID,
            src: isLibraryVideo(slide)
                ? (slide.LibraryImage?.LibraryImageFile?.VideoCover?.Desktop ??
                  '')
                : (slide.LibraryImageFile?.DetailPageNatural?.Desktop ?? ''),
            ratio: getSlideRatio(slide),
            multiplied: !isLibraryVideo(slide) && Boolean(slide.IsMultiplied),
        }))
        .filter((slide) => slide.src);

    return { link, title: node.Title, slides };
};
